import {
  qualifiesAsTourPackage,
  type ItineraryComponentOut,
  type PaymentScheduleItem,
} from '@utazzo/shared';
import {
  UzAlert,
  UzBadge,
  UzButton,
  UzCheckbox,
  UzDatePicker,
  UzInput,
  UzNumberInput,
} from '@utazzo/ui';
import {
  BedDouble,
  Car,
  Check,
  Link2,
  Lock,
  Plane,
  Plus,
  Sparkles,
  Ticket,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { type AdminPaymentPlanView } from '../itineraries.api';
import * as itinerariesApi from '../itineraries.api';
import {
  useClearReprice,
  useFdReprice,
  useItineraryComponents,
  useItineraryDetail,
  useItineraryPaymentPlan,
  useMarkupSuggestion,
  useRepriceItinerary,
  useUpdateItinerary,
} from '../use-itineraries';
import {
  useProposeInstalmentDateChange,
  useProposeInstalmentSplit,
} from '../use-reprice-approvals';

import { isFdLandPackage, isFdSupplement } from './ComponentCard';
import { FamilyBreakdownTable } from './FamilyBreakdownTable';
import { computeItineraryTaxTotals } from './pricing-totals';

import { useAdditionalDiscountsList } from '@/features/additional-discounts/use-additional-discounts';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useDestination } from '@/features/destinations/use-destinations';
import { useDiscountsList } from '@/features/discounts/use-discounts';
import { useLead } from '@/features/leads/use-leads';
import { useAppConfig } from '@/features/settings/use-settings';
import { extractServerError } from '@/lib/api-error';

/**
 * Pricing tab (before Preview).
 *
 * MARKUP has two tiers:
 *   - Global / product markup, per category, from the markup profiles. ADMIN
 *     ONLY - sales agents never see it or its breakdown, but it still applies to
 *     the total (bundled into the "Package price" they see).
 *   - The sales agent's OVERALL markup, added on top. Every role can set it.
 *
 * TAX: GST always applies (on the markup only, after discount). TCS applies to
 * INTERNATIONAL trips only - a domestic trip is GST-only. Rates come from the
 * itinerary's create-time snapshot (falling back to live AppConfig).
 */

type Cat = 'HOTEL' | 'FLIGHT' | 'ACTIVITY' | 'TRANSFER' | 'ADDON';

/**
 * The per-category cost / markup breakdown under Total Cost is ADMIN-only.
 * Non-admins (sales agent / lead) see only the rolled-up Total Cost headline -
 * never a per-category cost line (Activities, Add-ons, Flights, Hotels,
 * Transfers), each of which would expose supplier cost + markup.
 */

// Cruise is priced and marked-up as an in-house hotel stay (server folds
// CRUISE into the HOTEL markup bucket), so its cost + markup roll up under
// "Hotels" here - no separate Cruises category, to stay consistent with the
// server and avoid a double-count.
const CATS: { key: Cat; label: string; kinds: string[]; Icon: typeof Plane }[] = [
  { key: 'HOTEL', label: 'Hotels', kinds: ['HOTEL', 'CRUISE'], Icon: BedDouble },
  { key: 'FLIGHT', label: 'Flights', kinds: ['FLIGHT'], Icon: Plane },
  { key: 'ACTIVITY', label: 'Activities', kinds: ['ACTIVITY'], Icon: Ticket },
  { key: 'TRANSFER', label: 'Transfers', kinds: ['TRANSFER'], Icon: Car },
  { key: 'ADDON', label: 'Add-ons', kinds: ['VAS'], Icon: Sparkles },
];

const sumCost = (comps: ItineraryComponentOut[], kinds: string[]): number =>
  comps.filter((c) => kinds.includes(c.kind)).reduce((s, c) => s + (Number(c.costPaise) || 0), 0);

// Rupee <-> paise conversion for the manual Upsell input. Shows '' (not 0)
// so the field reads as empty/placeholder rather than a literal zero.
const paiseToRupees = (paise: number): number | '' => (paise > 0 ? paise / 100 : '');
const rupeesToPaise = (rupees: number | null): number =>
  rupees === null ? 0 : Math.round(rupees * 100);

export const PricingStep = ({
  itineraryId,
  editable,
}: {
  itineraryId: string;
  editable: boolean;
}): React.JSX.Element => {
  const detail = useItineraryDetail(itineraryId).data ?? null;
  const comps = useItineraryComponents(itineraryId).data ?? [];
  const config = useAppConfig().data ?? null;
  const update = useUpdateItinerary();
  const me = useCurrentUser();
  const role = me?.role;
  const isAdmin = role === 'ADMIN';
  // Sales lead / agent must not see GST or TCS lines or the tax rate banner.
  // Tax still applies in the grand total; only the breakdown is hidden.
  const canSeeTax = role !== 'SALES_LEAD' && role !== 'SALES_AGENT';

  // Domestic vs international drives TCS. Only a destination master EXPLICITLY
  // flagged domestic suppresses TCS; unknown/loading is treated as international
  // so we never silently undercharge.
  const lead = useLead(detail?.leadId ?? undefined).data ?? null;
  const dest = useDestination(lead?.destinationCity?.id ?? undefined).data ?? null;
  // TCS applies only to an INTERNATIONAL "overseas tour package" - >=2 of
  // {flight}/{hotel,cruise}/{activity,transfer,VAS}. Standalone ticket or stay
  // is not a package, so no TCS. Mirrors the server.
  const applyTcs =
    dest?.isInternational !== false && qualifiesAsTourPackage(comps.map((c) => c.kind));

  const currency = detail?.currencyCode ?? 'INR';
  const gstRate = detail?.gstRatePercent ?? config?.tax.gstRatePercent ?? 5;
  const tcsRate = detail?.tcsRatePercent ?? Number(config?.tax.tcsRatePercent ?? '2');

  const fmt = (paise: number): string => {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(paise / 100);
    } catch {
      return `${currency} ${(paise / 100).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  };

  // Per-category global markup (admin) + the agent overall markup + discount
  // / upsell. Upsell adds cost, discount subtracts. Discount is now set ONLY
  // via reprice (below) for a non-FD itinerary, or via the legacy master
  // dropdown for FD (which has no reprice); upsell is a manual rupee amount
  // the operator types directly - see the legacy-lock block below for how a
  // pre-existing percent-system value is preserved untouched.
  const hydrated = useRef(false);
  const persistedRef = useRef(false);
  const [markup, setMarkup] = useState<Record<Cat, number>>({
    HOTEL: 0,
    FLIGHT: 0,
    ACTIVITY: 0,
    TRANSFER: 0,
    ADDON: 0,
  });
  const [agentMarkup, setAgentMarkup] = useState(0);
  const [discountId, setDiscountId] = useState<string>('');
  // Manual upsell amount (rupees typed by the operator, held here as paise).
  // Unlike discount, upsell is no longer picked from a master list - any
  // positive amount is accepted.
  const [additionalDiscountManualPaise, setAdditionalDiscountManualPaise] = useState(0);
  // Snapshot of the discount/upsell exactly as loaded from the itinerary
  // (paise amount + legacy percent, if any + master id). Captured once on
  // hydrate and never mutated afterwards - it is what gets sent back
  // untouched when the field is left locked/unselected.
  const [discountSnapshot, setDiscountSnapshot] = useState<{
    paise: number;
    percent: number | null;
    id: string | null;
  }>({ paise: 0, percent: null, id: null });
  const [additionalDiscountSnapshot, setAdditionalDiscountSnapshot] = useState<{
    paise: number;
    percent: number | null;
    id: string | null;
  }>({ paise: 0, percent: null, id: null });
  // True once the operator has explicitly interacted with the field (picked
  // a fixed-amount master, chose "No discount/upsell", or hit "Clear to
  // change" on a locked field). Until then the snapshot values are used
  // verbatim - so simply opening and re-saving an itinerary never mutates
  // a legacy (or any other) stored discount/upsell.
  const [discountTouched, setDiscountTouched] = useState(false);
  const [additionalDiscountTouched, setAdditionalDiscountTouched] = useState(false);
  // Customer-facing payment plan for the docket ("First Installment ...").
  const [schedule, setSchedule] = useState<PaymentScheduleItem[]>([]);

  // Reprice (Task 4): the operator's typed target, in rupees (input is
  // rupee-denominated; converted to paise only on Apply). The reprice
  // mutations live at itineraryId scope, not per-field, since exactly one
  // of discount/upsell is solved server-side.
  const [repriceRupees, setRepriceRupees] = useState<number | ''>('');
  const reprice = useRepriceItinerary(itineraryId);
  const clearReprice = useClearReprice(itineraryId);

  // FD removal-reprice (visa / insurance / other-discount). Local checkbox +
  // amount state; applied through useFdReprice which re-solves from the clean
  // quote each call. Seeded from the stored marker below (in an effect) so the
  // panel reflects an already-applied removal.
  const fdReprice = useFdReprice(itineraryId);
  const [rmVisa, setRmVisa] = useState(false);
  const [rmInsurance, setRmInsurance] = useState(false);
  // Seed the checkboxes from the stored FD removal marker (once per marker
  // change) so an already-applied removal shows its state on load / refetch.
  // "Other discount" is intentionally NOT offered here - the reprice discount
  // section below is the single place a manual discount is entered.
  const fdMarkerSeed = detail?.fdRepriceRemovals ?? null;
  useEffect(() => {
    setRmVisa(Boolean(fdMarkerSeed?.visa));
    setRmInsurance(Boolean(fdMarkerSeed?.insurance));
  }, [fdMarkerSeed]);

  const discountsQuery = useDiscountsList({
    page: 1,
    pageSize: 100,
    isActive: true,
    sortBy: 'sortOrder',
    sortDir: 'asc',
  });
  const additionalDiscountsQuery = useAdditionalDiscountsList({
    page: 1,
    pageSize: 100,
    isActive: true,
    sortBy: 'sortOrder',
    sortDir: 'asc',
  });
  const discountMasters = discountsQuery.data?.data ?? [];
  const additionalDiscountMasters = additionalDiscountsQuery.data?.data ?? [];
  const mastersLoaded = discountsQuery.data !== undefined;
  const additionalMastersLoaded = additionalDiscountsQuery.data !== undefined;
  // Only fixed-amount masters are offerable from the dropdown - a master
  // still sitting at the Task-1 migration default of 0 paise is a
  // percent-only legacy row and would be a silent no-op discount.
  // Still used by the FD discount dropdown below (FD has no reprice, so it
  // keeps the old master-pick discount UI).
  const discountOptions = useMemo(
    () =>
      discountMasters
        .filter((d) => Number(d.amountPaise) > 0)
        .map((d) => ({
          value: d.id,
          label: `${d.name} (Rs ${Math.round(Number(d.amountPaise) / 100).toLocaleString('en-IN')})`,
          amountPaise: Number(d.amountPaise),
        })),
    [discountMasters],
  );

  // A stored discount/upsell is LEGACY (from the old percent-of-markup
  // system) - and must render locked/read-only rather than be re-derived -
  // when any of: (a) it still carries a percent snapshot, (b) its master id
  // resolves to a master with no fixed amount (a percent-only master), or
  // (c) it has a positive stored amount but no master id at all. While the
  // master list is still loading, an id-bearing snapshot defaults to locked
  // (fail safe) rather than briefly rendering as editable.
  const isLegacySnapshot = (
    snap: { paise: number; percent: number | null; id: string | null },
    masters: { id: string; amountPaise: string }[],
    listLoaded: boolean,
  ): boolean => {
    if (snap.paise <= 0) return false;
    if (snap.percent !== null && snap.percent !== undefined) return true;
    if (snap.id) {
      if (!listLoaded) return true;
      const resolved = masters.find((m) => m.id === snap.id);
      return !resolved || Number(resolved.amountPaise) <= 0;
    }
    return true;
  };
  const discountLocked =
    !discountTouched && isLegacySnapshot(discountSnapshot, discountMasters, mastersLoaded);
  const additionalDiscountLocked =
    !additionalDiscountTouched &&
    isLegacySnapshot(
      additionalDiscountSnapshot,
      additionalDiscountMasters,
      additionalMastersLoaded,
    );

  // Reprice (Task 4): a STRONGER lock than the legacy lock above. When a
  // reprice is active (`repriceTargetPaise` set), the solved lever's field
  // is shown locked with a "Clear reprice" affordance instead of "Clear to
  // change" - and the ordinary dropdown / legacy-lock rendering for BOTH
  // discount and upsell is superseded entirely (see the JSX below, which
  // checks `repriceActive` first). Pricing (grand total, discount, upsell)
  // is read straight from `detail` while a reprice is active - see
  // `useFrozenPricing` below - so nothing here needs to re-derive money.
  const repriceActive = Boolean(detail?.repriceTargetPaise);
  const repriceLever = detail?.repriceLever ?? null;
  const repriceTargetPaiseNum = detail?.repriceTargetPaise
    ? Number(detail.repriceTargetPaise)
    : null;
  const repriceAchievedPaiseNum = detail?.repriceAchievedGrandPaise
    ? Number(detail.repriceAchievedGrandPaise)
    : null;
  // Only ~Re 1 apart at most (whole-rupee rounding) but shown whenever they
  // differ at all, so the operator always sees the EXACT achieved figure.
  const repriceClosestAchievable =
    repriceTargetPaiseNum !== null &&
    repriceAchievedPaiseNum !== null &&
    repriceTargetPaiseNum !== repriceAchievedPaiseNum;
  const repriceNetMarkupPaise = Number(detail?.netMarkupPaise ?? 0) || 0;
  const repriceDiscountPaise =
    repriceLever === 'DISCOUNT' ? Number(detail?.discountPaise ?? 0) || 0 : 0;
  const repriceUpsellPaise =
    repriceLever === 'UPSELL' ? Number(detail?.additionalDiscountPaise ?? 0) || 0 : 0;
  // Prefer the just-applied mutation's own figures (exact server response);
  // fall back to the same formula the server uses (mirrored client-side,
  // per the "client mirrors for preview only" constraint) so the note still
  // renders correctly after a remount / reload while a reprice stays active.
  const repriceMarginPct =
    reprice.data?.marginPct ??
    (repriceLever === 'DISCOUNT' && repriceNetMarkupPaise > 0
      ? (repriceDiscountPaise / repriceNetMarkupPaise) * 100
      : 0);
  const repriceNeedsApproval =
    reprice.data?.needsApproval ?? (repriceLever === 'DISCOUNT' && repriceMarginPct > 2);
  const repriceErrorMessage = reprice.isError
    ? (extractServerError(reprice.error).message ?? 'Could not reprice - please try again.')
    : null;
  // Approval-status messages are shown only to people INVOLVED in the approval:
  // the requester who submitted it, the assigned reviewer, or an admin - not to
  // unrelated viewers of the itinerary.
  const repriceApprovalRow = detail?.repriceApproval ?? null;
  const isRepriceParticipant =
    !!me &&
    !!repriceApprovalRow &&
    (me.id === repriceApprovalRow.requestedById ||
      me.role === 'ADMIN' ||
      (me.role === 'SALES_LEAD' && repriceApprovalRow.assignedReviewerId === me.id));
  // A REJECTED reprice is cleared server-side (repriceActive goes false), so
  // the agent otherwise has no way to see why their reprice didn't go through.
  // Surfaced regardless of repriceActive, but only to a participant.
  const rejectedRepriceApproval =
    isRepriceParticipant && repriceApprovalRow?.status === 'REJECTED' ? repriceApprovalRow : null;

  // Effective fixed amount to preview/save: the just-picked master's amount
  // once touched, otherwise the exact-as-loaded snapshot (covers both the
  // locked-legacy case and an untouched, already-fixed-amount selection).
  // Discount is only master-picked on FD (see `discountOptions` above); on
  // non-FD it is set via reprice instead and this formula falls through to
  // the snapshot, which the reprice apply/clear flow keeps in sync via
  // `detail`.
  const discountAmountPaise = discountTouched
    ? discountId
      ? (discountOptions.find((o) => o.value === discountId)?.amountPaise ?? 0)
      : 0
    : discountSnapshot.paise;
  // Upsell is a manually typed amount, not a master pick - once touched, use
  // whatever the operator typed verbatim.
  const additionalDiscountAmountPaise = additionalDiscountTouched
    ? additionalDiscountManualPaise
    : additionalDiscountSnapshot.paise;

  const suggestion = useMarkupSuggestion(itineraryId).data ?? null;
  const suggested = (): Record<Cat, number> => {
    const mbc = suggestion?.markupByCategory ?? {};
    return {
      // Server folds any CRUISE markup into HOTEL, so fold a legacy stored
      // CRUISE bucket back into HOTEL on read for older rows.
      HOTEL: (mbc.HOTEL ?? 0) + (mbc.CRUISE ?? 0),
      FLIGHT: mbc.FLIGHT ?? 0,
      ACTIVITY: mbc.ACTIVITY ?? 0,
      TRANSFER: mbc.TRANSFER ?? 0,
      ADDON: mbc.ADDON ?? 0,
    };
  };

  // Re-seed when the itinerary id changes (new version / version switch).
  // Without this, a long-lived PricingStep keeps the previous version's
  // empty-or-stale discount selection after copy-forward.
  useEffect(() => {
    hydrated.current = false;
    persistedRef.current = false;
  }, [itineraryId]);

  // Hydrate non-markup fields once from the detail snapshot. Per-category
  // markup is handled by the effect below, which prefers the LIVE suggestion
  // for draft itineraries.
  useEffect(() => {
    if (hydrated.current || !detail || detail.id !== itineraryId) return;
    hydrated.current = true;
    setAgentMarkup(Number(detail.agentMarkupPaise ?? 0) || 0);
    const nextDiscountId = detail.discountId ?? '';
    const nextUpsellId = detail.additionalDiscountId ?? '';
    const discountPercentRaw =
      detail.discountPercent !== null && detail.discountPercent !== undefined
        ? Number(detail.discountPercent)
        : null;
    const upsellPercentRaw =
      detail.additionalDiscountPercent !== null && detail.additionalDiscountPercent !== undefined
        ? Number(detail.additionalDiscountPercent)
        : null;
    setDiscountSnapshot({
      paise: Number(detail.discountPaise ?? 0) || 0,
      percent: discountPercentRaw,
      id: nextDiscountId || null,
    });
    setAdditionalDiscountSnapshot({
      paise: Number(detail.additionalDiscountPaise ?? 0) || 0,
      percent: upsellPercentRaw,
      id: nextUpsellId || null,
    });
    setDiscountTouched(false);
    setAdditionalDiscountTouched(false);
    // `nextDiscountId` only matters for FD, which keeps the master-pick
    // dropdown (non-FD sets discount via reprice, never through this id).
    setDiscountId(nextDiscountId);
    // Upsell is a manual amount now, not a master pick - seed the input from
    // the stored paise so an existing (non-legacy) amount is editable as-is.
    setAdditionalDiscountManualPaise(Number(detail.additionalDiscountPaise ?? 0) || 0);
    setSchedule(detail.paymentSchedule ?? []);
  }, [detail, itineraryId]);

  // Per-category markup source of truth:
  //  - If the itinerary already has `markupByCategory` stored (which is the
  //    case for a copy-forward version, or any DRAFT/IN_PROGRESS itinerary
  //    that has been saved once) -> use those stored numbers verbatim. The
  //    live suggestion is deliberately NOT fetched here so a copy-forward
  //    quote never gets its baked (discount + upsell folded into buckets)
  //    values overwritten by a fresh 17% OVERALL recompute.
  //  - If `markupByCategory` is null AND the itinerary is DRAFT/IN_PROGRESS
  //    (a first-from-lead or blank version) -> fetch the live suggestion and
  //    seed the state + persist it once via a fire-and-forget update so the
  //    DB matches what the operator sees.
  //  - Any other status -> frozen snapshot on the itinerary detail, same
  //    as always.
  const isDraftLive = detail?.status === 'DRAFT' || detail?.status === 'IN_PROGRESS';
  useEffect(() => {
    if (!detail) return;
    const storedMbc = detail.markupByCategory;
    if (storedMbc) {
      // Already populated (copied version, saved draft, or non-draft) - use it verbatim.
      setMarkup({
        // Fold any legacy stored CRUISE bucket into HOTEL (server reuse-HOTEL).
        HOTEL: (storedMbc.HOTEL ?? 0) + (storedMbc.CRUISE ?? 0),
        FLIGHT: storedMbc.FLIGHT ?? 0,
        ACTIVITY: storedMbc.ACTIVITY ?? 0,
        TRANSFER: storedMbc.TRANSFER ?? 0,
        ADDON: storedMbc.ADDON ?? 0,
      });
      return;
    }
    if (!isDraftLive) return;
    // No stored markup on a fresh draft: seed from live suggestion.
    if (!suggestion) return;
    const next = suggested();
    setMarkup(next);
    if (!persistedRef.current) {
      persistedRef.current = true;
      const nextGlobal = CATS.reduce((s, c) => s + (next[c.key] || 0), 0);
      const nextAgent = Number(detail.agentMarkupPaise ?? 0) || 0;
      void itinerariesApi.updateItinerary(detail.id, {
        markupByCategory: next,
        netMarkupPaise: String(nextGlobal + nextAgent),
      });
    }
  }, [detail, suggestion, isDraftLive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Visa cost is FX-converted server-side into the itinerary currency (see
  // itineraries.service#computeVisaCostPaise). It rolls into the ADDON
  // bucket so it flows through the same category markup + tax logic as any
  // manually-added VAS component.
  const visaCost = Number(detail?.visaCostPaise ?? 0) || 0;
  const insuranceCost = Number(detail?.insuranceCostPaise ?? 0) || 0;
  const addOnFrozenCost = visaCost + insuranceCost;
  const costByCat = Object.fromEntries(
    CATS.map((c) => [c.key, sumCost(comps, c.kinds) + (c.key === 'ADDON' ? addOnFrozenCost : 0)]),
  ) as Record<Cat, number>;
  const totalCost = CATS.reduce((s, c) => s + costByCat[c.key], 0);
  const globalMarkup = CATS.reduce((s, c) => s + (markup[c.key] || 0), 0);

  // Fixed-departure breakdown: the land price is a VAS and supplements are
  // TRANSFERs, so the generic buckets mislabel them ("Add-ons cost" for the
  // land, "Transfers cost" for the supplement). Recategorize by FD semantics and
  // spread the product markup proportionally by cost, matching the Preview
  // sidebar. FIT keeps the bucket rows.
  const isFd = detail?.kind === 'FIXED_DEPARTURE';
  const fdCostWhere = (pred: (c: ItineraryComponentOut) => boolean): number =>
    comps.filter(pred).reduce((s, c) => s + (Number(c.costPaise) || 0), 0);
  const fdRows = ((): { label: string; cost: number; markup: number }[] => {
    if (!isFd) return [];
    const land = fdCostWhere(isFdLandPackage);
    const supplements = fdCostWhere(isFdSupplement);
    const flights = costByCat.FLIGHT;
    const addOns = fdCostWhere((c) => c.kind === 'VAS' && !isFdLandPackage(c)) + addOnFrozenCost;
    // Markup per line = the ACTUAL per-category bucket, NOT a proportional spread.
    // So flights show their own rule (e.g. global 2%) while land/supplements show
    // the FD rate. Land + Add-ons share the ADDON/HOTEL buckets (same FD rate),
    // split by cost; the Add-ons row absorbs the remainder so the rows always
    // reconcile to the total.
    const flightMk = markup.FLIGHT || 0;
    const supMk = markup.TRANSFER || 0;
    const landAddonMk = (markup.ADDON || 0) + (markup.HOTEL || 0);
    const landAddonCost = land + addOns;
    const landMk = landAddonCost > 0 ? Math.round((landAddonMk * land) / landAddonCost) : 0;
    const rows = [
      { label: 'Land package', cost: land, markup: landMk },
      { label: 'Vehicle supplements', cost: supplements, markup: supMk },
      { label: 'Flights', cost: flights, markup: flightMk },
    ];
    const addOnMk = globalMarkup - rows.reduce((s, r) => s + r.markup, 0);
    rows.push({ label: 'Add-ons', cost: addOns, markup: addOnMk });
    return rows.filter((r) => r.cost > 0);
  })();

  // FD removal-reprice: which supplements can be dropped, and what's already
  // dropped (from the stored marker). A removed supplement is soft-deleted so
  // it's gone from `comps` - fall back to the marker for its label / amount.
  const fdSupplementKindOf = (c: ItineraryComponentOut): string | undefined =>
    (c.metaJson as { fdSupplementKind?: string } | null | undefined)?.fdSupplementKind;
  const fdVisaComp = isFd ? comps.find((c) => fdSupplementKindOf(c) === 'VISA') : undefined;
  const fdInsuranceComp = isFd
    ? comps.find((c) => fdSupplementKindOf(c) === 'INSURANCE')
    : undefined;
  const fdRemovals = detail?.fdRepriceRemovals ?? null;
  const visaLegacyPaise = Number(detail?.visaCostPaise ?? 0) || 0;
  const insuranceLegacyPaise = Number(detail?.insuranceCostPaise ?? 0) || 0;
  const canRemoveVisa =
    isFd && (Boolean(fdVisaComp) || visaLegacyPaise > 0 || Boolean(fdRemovals?.visa));
  const canRemoveInsurance =
    isFd &&
    (Boolean(fdInsuranceComp) || insuranceLegacyPaise > 0 || Boolean(fdRemovals?.insurance));
  const visaRemovalLabel = fdVisaComp?.title ?? fdRemovals?.visa?.label ?? 'Visa';
  const insuranceRemovalLabel =
    fdInsuranceComp?.title ?? fdRemovals?.insurance?.label ?? 'Insurance';
  const visaRemovalPaise = Number(
    fdVisaComp?.costPaise ?? fdRemovals?.visa?.amountPaise ?? visaLegacyPaise,
  );
  const insuranceRemovalPaise = Number(
    fdInsuranceComp?.costPaise ?? fdRemovals?.insurance?.amountPaise ?? insuranceLegacyPaise,
  );
  const fdRemovalActive = Boolean(
    fdRemovals && (fdRemovals.visa || fdRemovals.insurance || fdRemovals.otherDiscountPaise),
  );

  // Discount / Upsell are fixed rupee amounts (Task 2) - `discountAmountPaise`
  // / `additionalDiscountAmountPaise` already resolve to either the just-picked
  // master's amount or the exact-as-loaded snapshot (locked-legacy or an
  // untouched existing selection). No percent-of-markup conversion here.
  const discountPaise = discountAmountPaise;
  const additionalDiscountPaise = additionalDiscountAmountPaise;

  const taxTotals = computeItineraryTaxTotals({
    netCostPaise: totalCost,
    // Flight base fare - excluded from the TCS base (TCS on flight margin only).
    flightComponentPaise: costByCat.FLIGHT,
    globalMarkupPaise: globalMarkup,
    agentMarkupPaise: agentMarkup,
    discountPaise,
    additionalDiscountPaise,
    gstRatePercent: gstRate,
    tcsRatePercent: tcsRate,
    applyTcs,
  });

  // Draft / in-progress: live math (same as editing). Sent-to-customer and
  // later statuses: freeze discount / tax / grand total from the DB snapshot
  // so admin Preview and Pricing match what the customer was quoted. A reprice
  // ALSO freezes pricing (even on a draft): the server already back-solved
  // and persisted the exact discount/upsell + grand total, so the Quote total
  // sidebar reads that straight from `detail` instead of re-deriving it here
  // (Task 4 constraint: "no manual math here").
  const useFrozenPricing = Boolean(detail) && (!isDraftLive || repriceActive);
  const effectiveAgentMarkup = useFrozenPricing
    ? Number(detail?.agentMarkupPaise ?? 0) || 0
    : taxTotals.effectiveAgentMarkupPaise;
  const totalMarkup = useFrozenPricing
    ? Number(detail?.netMarkupPaise ?? 0) || 0
    : taxTotals.totalMarkupPaise;
  const frozenNetCost = useFrozenPricing ? Number(detail?.netCostPaise ?? 0) || 0 : totalCost;
  const systemPct = frozenNetCost > 0 ? (globalMarkup / frozenNetCost) * 100 : 0;
  const effectiveDiscount = useFrozenPricing
    ? Number(detail?.discountPaise ?? 0) || 0
    : taxTotals.effectiveDiscountPaise;
  const effectiveAdditionalDiscount = useFrozenPricing
    ? Number(detail?.additionalDiscountPaise ?? 0) || 0
    : taxTotals.effectiveAdditionalDiscountPaise;
  const gst = useFrozenPricing ? Number(detail?.gstPaise ?? 0) || 0 : taxTotals.gstPaise;
  const tcs = useFrozenPricing ? Number(detail?.tcsPaise ?? 0) || 0 : taxTotals.tcsPaise;
  const grandTotal = useFrozenPricing
    ? Number(detail?.grandTotalPaise ?? 0) || 0
    : taxTotals.grandTotalPaise;
  // Signed whole-rupee rounding adjustment folded into the grand total, shown as
  // its own line so the breakdown ties out to the rounded (charged) total.
  const roundOff = useFrozenPricing
    ? Number(detail?.roundOffPaise ?? 0) || 0
    : taxTotals.roundOffPaise;
  const packagePrice = useFrozenPricing
    ? frozenNetCost + totalMarkup - effectiveAgentMarkup
    : taxTotals.packagePricePaise;

  const save = (): void => {
    const cleanSchedule = schedule
      .filter((s) => s.label.trim() && Number(s.amountPaise) > 0)
      .map((s) => ({
        label: s.label.trim(),
        amountPaise: s.amountPaise,
        dueDate: s.dueDate || null,
      }));
    // Discount / Upsell: while untouched, send the AS-LOADED snapshot back
    // verbatim (paise, id, percent) - this is what keeps a legacy (or any
    // other) stored value from ever drifting just because the operator
    // opened and re-saved the pricing tab. Only an explicit pick or "Clear
    // to change" (which sets *Touched) derives a fresh fixed amount and
    // nulls the percent. While a reprice is active the dropdowns are hidden
    // entirely (stronger than the legacy lock - see JSX below), so echo the
    // server's own solved amounts back untouched: a "Save pricing" click for
    // an unrelated field (e.g. the payment schedule) must never clobber the
    // active reprice.
    const discountFields = repriceActive
      ? {
          discountPaise: String(detail?.discountPaise ?? '0'),
          discountId: null,
          discountPercent: null,
        }
      : discountTouched
        ? {
            discountPaise: String(discountId ? discountAmountPaise : 0),
            discountId: discountId || null,
            discountPercent: null,
          }
        : {
            discountPaise: String(discountSnapshot.paise),
            discountId: discountSnapshot.id,
            discountPercent: discountSnapshot.percent,
          };
    const upsellFields = repriceActive
      ? {
          additionalDiscountPaise: String(detail?.additionalDiscountPaise ?? '0'),
          additionalDiscountId: null,
          additionalDiscountPercent: null,
        }
      : additionalDiscountTouched
        ? {
            // Manual amount, not a master pick - no id to send.
            additionalDiscountPaise: String(additionalDiscountManualPaise),
            additionalDiscountId: null,
            additionalDiscountPercent: null,
          }
        : {
            additionalDiscountPaise: String(additionalDiscountSnapshot.paise),
            additionalDiscountId: additionalDiscountSnapshot.id,
            additionalDiscountPercent: additionalDiscountSnapshot.percent,
          };
    update.mutate({
      id: itineraryId,
      payload: {
        // Global markup: admin edits it; sales leaves it as hydrated.
        markupByCategory: markup,
        // Include visa cost (FX-converted) in the persisted net cost so the
        // publish snapshot + payment link amount both cover the visa charge.
        // recomputeItineraryTotals server-side sums components only; visa is
        // an itinerary-level add-on, so the client sends the full total here.
        netCostPaise: String(totalCost),
        agentMarkupPaise: String(effectiveAgentMarkup),
        netMarkupPaise: String(totalMarkup),
        ...discountFields,
        ...upsellFields,
        gstPaise: String(gst),
        tcsPaise: String(tcs),
        paymentSchedule: cleanSchedule.length > 0 ? cleanSchedule : null,
      },
    });
  };

  const setScheduleItem = (i: number, patch: Partial<PaymentScheduleItem>): void =>
    setSchedule((s) => s.map((item, j) => (j === i ? { ...item, ...patch } : item)));
  const scheduleTotal = schedule.reduce((s, item) => s + (Number(item.amountPaise) || 0), 0);

  return (
    <div className="grid items-start gap-6 pb-20 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-6">
        {/* 2026-07-07. Per-category global markup section is hidden entirely
            (previously admin-only). The values still apply to the grand total,
            hydrated from the markup master via `useMarkupSuggestion` on first
            landing and persisted by `save()`. Removing the UI keeps operators
            focused on the agent-overall-markup and discount inputs; admins can
            manage suggested markup in the markup master, not per itinerary. */}

        {/* FD removal-reprice: drop the visa / insurance supplement or add an
            "other" discount. FD-only; each removal deducts its exact cost and
            moves the inclusion line to exclusions. Routes through the same
            approval queue as a target reprice. */}
        {isFd && (canRemoveVisa || canRemoveInsurance || fdRemovalActive) && (
          <section className="rounded-lg border border-zinc-200 bg-white p-5">
            <p className="text-sm font-semibold text-zinc-900">Reprice: remove items</p>
            <p className="mt-0.5 text-xs text-zinc-500">
              Drop the visa / insurance the customer doesn&apos;t want - its exact amount leaves the
              total and the item moves to exclusions.
            </p>

            <div className="mt-3 space-y-2.5">
              {canRemoveVisa && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rmVisa}
                    disabled={!editable}
                    onChange={(e) => setRmVisa(e.target.checked)}
                  />
                  <span className="font-medium text-zinc-800">Remove {visaRemovalLabel}</span>
                  <span className="text-zinc-500 tabular-nums">-{fmt(visaRemovalPaise)}</span>
                </label>
              )}
              {canRemoveInsurance && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rmInsurance}
                    disabled={!editable}
                    onChange={(e) => setRmInsurance(e.target.checked)}
                  />
                  <span className="font-medium text-zinc-800">Remove {insuranceRemovalLabel}</span>
                  <span className="text-zinc-500 tabular-nums">-{fmt(insuranceRemovalPaise)}</span>
                </label>
              )}
            </div>

            {editable && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <UzButton
                  onClick={() =>
                    fdReprice.mutate({
                      removeVisa: rmVisa,
                      removeInsurance: rmInsurance,
                      otherDiscountPaise: '0',
                      otherDiscountLabel: null,
                    })
                  }
                  loading={fdReprice.isPending}
                >
                  Apply reprice
                </UzButton>
                {fdRemovalActive && (
                  <UzButton
                    variant="secondary"
                    onClick={() => clearReprice.mutate()}
                    loading={clearReprice.isPending}
                  >
                    Clear removals
                  </UzButton>
                )}
                {extractServerError(fdReprice.error)?.message && (
                  <span className="text-xs text-rose-600">
                    {extractServerError(fdReprice.error)?.message}
                  </span>
                )}
              </div>
            )}

            {/* What was removed (from the stored marker) - visible to everyone. */}
            {fdRemovalActive && (
              <div className="mt-4 rounded-md bg-zinc-50 p-3 text-sm">
                <p className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                  Removed from this booking
                </p>
                <ul className="mt-1.5 space-y-1">
                  {fdRemovals?.visa && (
                    <li className="flex justify-between">
                      <span className="text-zinc-700">{fdRemovals.visa.label}</span>
                      <span className="text-zinc-500 tabular-nums">
                        -{fmt(Number(fdRemovals.visa.amountPaise))}
                      </span>
                    </li>
                  )}
                  {fdRemovals?.insurance && (
                    <li className="flex justify-between">
                      <span className="text-zinc-700">{fdRemovals.insurance.label}</span>
                      <span className="text-zinc-500 tabular-nums">
                        -{fmt(Number(fdRemovals.insurance.amountPaise))}
                      </span>
                    </li>
                  )}
                  {Number(fdRemovals?.otherDiscountPaise ?? 0) > 0 && (
                    <li className="flex justify-between">
                      <span className="text-zinc-700">
                        Other discount
                        {fdRemovals?.otherDiscountLabel
                          ? ` - ${fdRemovals.otherDiscountLabel}`
                          : ''}
                      </span>
                      <span className="text-zinc-500 tabular-nums">
                        -{fmt(Number(fdRemovals?.otherDiscountPaise ?? 0))}
                      </span>
                    </li>
                  )}
                </ul>
                {repriceApprovalRow?.status === 'PENDING' && (
                  <UzAlert tone="warning" className="mt-3">
                    This reprice needs approval before it can be published or shared.
                  </UzAlert>
                )}
              </div>
            )}
          </section>
        )}

        {/* Discount + tax note. System / agent / final markup stay visible only
            in the Quote total sidebar on the right - operators shouldn't edit
            them here (values still apply from markup master + saved agent bump). */}
        <section className="rounded-lg border border-zinc-200 bg-white p-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              {/* Discount is set ONLY via reprice, for every itinerary kind
                  (FD included). The reprice input + its achieved-total/margin/
                  approval messaging used to live in a separate "Reprice to exact
                  total" section under this grid - it now lives here instead,
                  since this is the only place a discount can be set. Existing
                  itineraries with a legacy discount show it locked/read-only. */}
              {repriceActive ? (
                <>
                  {repriceLever === 'DISCOUNT' ? (
                    <RepricedLockedField
                      label="Discount"
                      amountLabel={fmt(repriceDiscountPaise)}
                      editable={editable}
                      onClear={() => clearReprice.mutate()}
                      clearing={clearReprice.isPending}
                    />
                  ) : (
                    <UzInput
                      label="Discount (not used - reprice active)"
                      value="None"
                      disabled
                      readOnly
                    />
                  )}
                  <div className="mt-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <UzBadge tone="secondary">Repriced</UzBadge>
                      <span className="text-sm text-zinc-700">
                        Achieved total:{' '}
                        <span className="font-semibold tabular-nums">
                          {repriceAchievedPaiseNum !== null ? fmt(repriceAchievedPaiseNum) : '-'}
                        </span>
                      </span>
                      {/* The discount percentage exposes margin - ADMIN only. */}
                      {isAdmin && (
                        <span className="text-xs text-zinc-500">
                          Discount {repriceMarginPct.toFixed(2)}% of selling price
                        </span>
                      )}
                    </div>
                    {repriceClosestAchievable && (
                      <p className="mt-1 text-xs text-zinc-500">
                        Closest achievable - the requested target (
                        {repriceTargetPaiseNum !== null ? fmt(repriceTargetPaiseNum) : '-'})
                        can&apos;t be hit exactly because of GST/TCS rounding.
                      </p>
                    )}
                    {repriceNeedsApproval && (
                      <UzAlert tone="warning" className="mt-3">
                        {isAdmin
                          ? repriceMarginPct > 5
                            ? 'This discount exceeds 5% of the selling price and requires admin approval before publishing.'
                            : 'This discount exceeds 2% of the selling price and requires manager approval before publishing.'
                          : repriceMarginPct > 5
                            ? 'This discount requires admin approval before publishing.'
                            : 'This discount requires manager approval before publishing.'}
                      </UzAlert>
                    )}
                  </div>
                </>
              ) : discountLocked ? (
                <LockedMoneyField
                  label="Discount"
                  amountLabel={fmt(discountAmountPaise)}
                  editable={editable}
                  onClear={() => {
                    setDiscountTouched(true);
                    setDiscountId('');
                  }}
                />
              ) : (
                <div>
                  <p className="text-sm font-medium text-zinc-900">Discount (set via reprice)</p>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Back-solve the discount so the grand total lands on the exact figure the
                    customer will pay - useful when closing at a round number.
                  </p>
                  {editable && (
                    <div className="mt-2 flex flex-wrap items-end gap-2">
                      <div className="w-40">
                        <UzNumberInput
                          label="Reprice to (Rs)"
                          precision={0}
                          min={0}
                          value={repriceRupees}
                          onValueChange={(v) => setRepriceRupees(v ?? '')}
                        />
                      </div>
                      <UzButton
                        onClick={() => {
                          const rupees = Number(repriceRupees);
                          if (!rupees || rupees <= 0) return;
                          reprice.mutate(String(Math.round(rupees * 100)), {
                            onSuccess: () => setRepriceRupees(''),
                          });
                        }}
                        loading={reprice.isPending}
                        disabled={!repriceRupees}
                      >
                        Apply
                      </UzButton>
                    </div>
                  )}
                  {repriceErrorMessage && (
                    <UzAlert tone="destructive" className="mt-2">
                      {repriceErrorMessage}
                    </UzAlert>
                  )}
                </div>
              )}
              {/* A rejected reprice is cleared server-side (repriceActive is
                  false again), so this renders independent of the branches
                  above - it's the only place the agent learns why. */}
              {rejectedRepriceApproval && (
                <UzAlert tone="destructive" className="mt-3">
                  {rejectedRepriceApproval.reviewRemark
                    ? `Your reprice was rejected: ${rejectedRepriceApproval.reviewRemark}. Adjust and reprice again.`
                    : 'Your reprice was rejected. Adjust and reprice again.'}
                </UzAlert>
              )}
            </div>
            <div>
              {repriceActive ? (
                repriceLever === 'UPSELL' ? (
                  <RepricedLockedField
                    label="Upsell"
                    amountLabel={fmt(repriceUpsellPaise)}
                    editable={editable}
                    onClear={() => clearReprice.mutate()}
                    clearing={clearReprice.isPending}
                  />
                ) : (
                  <UzInput
                    label="Upsell (not used - reprice active)"
                    value="None"
                    disabled
                    readOnly
                  />
                )
              ) : additionalDiscountLocked ? (
                <LockedMoneyField
                  label="Upsell"
                  amountLabel={fmt(additionalDiscountAmountPaise)}
                  editable={editable}
                  onClear={() => {
                    setAdditionalDiscountTouched(true);
                    setAdditionalDiscountManualPaise(0);
                  }}
                />
              ) : (
                // Manual amount, not a master pick - the operator can type any
                // positive rupee figure and it saves as-is (no dropdown).
                <UzNumberInput
                  label="Upsell (Rs)"
                  precision={2}
                  min={0}
                  value={paiseToRupees(additionalDiscountAmountPaise)}
                  onValueChange={(v) => {
                    setAdditionalDiscountManualPaise(rupeesToPaise(v));
                    setAdditionalDiscountTouched(true);
                  }}
                  disabled={!editable}
                  hint={
                    additionalDiscountAmountPaise > 0
                      ? `= +${fmt(additionalDiscountAmountPaise)}`
                      : ''
                  }
                />
              )}
            </div>
          </div>
          {canSeeTax && (
            <div className="mt-4 rounded-md bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
              <p className="font-medium">
                Tax: GST {gstRate}%{applyTcs ? ` + TCS ${tcsRate}%` : ''}
              </p>
            </div>
          )}
          {canSeeTax && !config && (
            <UzAlert tone="info" className="mt-4">
              Loading tax rates from settings...
            </UzAlert>
          )}
        </section>

        <PaymentPlanSection
          itineraryId={itineraryId}
          isAdmin={isAdmin}
          editable={editable}
          schedule={schedule}
          setSchedule={setSchedule}
          setScheduleItem={setScheduleItem}
          scheduleTotal={scheduleTotal}
          grandTotal={grandTotal}
          currency={currency}
        />

        {editable && (
          <div className="flex justify-end">
            <UzButton onClick={save} loading={update.isPending}>
              <Check className="mr-1.5 h-4 w-4" aria-hidden />
              Save pricing
            </UzButton>
          </div>
        )}

        {/* Per-family split. Lives in the main column: the totals aside is
            sticky, so a full-width row below the grid gets overlapped by it. */}
        <FamilyBreakdownTable
          breakdown={detail?.familyBreakdown}
          isAdmin={isAdmin}
          canSeeTax={canSeeTax}
          fmt={fmt}
        />
      </div>

      {/* Live total */}
      <aside className="lg:sticky lg:top-4">
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
          <header className="border-b border-zinc-100 px-4 py-3">
            <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Quote total</h3>
          </header>
          <dl className="space-y-1.5 px-4 py-4 text-sm">
            {/* Per-category cost + markup (admin-only). Sales agents / leads never
                see any per-category cost - they start at Net Selling Price below. */}
            {isAdmin && (
              <div className="space-y-1.5">
                {(() => {
                  // Requested display order: Flight -> Hotel -> Transfer ->
                  // Activities -> Add-ons. Skip a category if it has no cost
                  // AND no markup so the panel stays tight on small quotes.
                  const ORDER: Cat[] = ['FLIGHT', 'HOTEL', 'TRANSFER', 'ACTIVITY', 'ADDON'];
                  const rows = isFd
                    ? fdRows.map((r) => ({
                        key: r.label,
                        label: r.label,
                        cost: r.cost,
                        markup: r.markup,
                      }))
                    : ORDER.map((k) => ({
                        key: k as string,
                        label: CATS.find((c) => c.key === k)!.label,
                        cost: costByCat[k] || 0,
                        markup: markup[k] || 0,
                      })).filter((r) => r.cost > 0 || r.markup > 0);
                  return rows.map((r, i) => {
                    // Percent shown next to the markup amount so admins can see
                    // "how much was added where" at a glance.
                    const pct =
                      r.cost > 0 && r.markup > 0
                        ? ` (${((r.markup / r.cost) * 100).toFixed(1)}%)`
                        : '';
                    return (
                      <React.Fragment key={r.key}>
                        <Line label={`${r.label} cost`}>{fmt(r.cost)}</Line>
                        <Line label={`Markup${pct}`} muted>
                          {fmt(r.markup)}
                        </Line>
                        {i < rows.length - 1 && (
                          <div className="border-t border-dashed border-zinc-200" />
                        )}
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            )}

            {/* Net Cost -> System markup -> Net Selling Price. Net Cost + System
                markup are admin-only read-outs already summed into the rows above;
                every role sees the Net Selling Price they roll up to. */}
            <div className="space-y-1.5 border-t border-zinc-100 pt-2">
              {isAdmin && <Line label="Net Cost">{fmt(totalCost)}</Line>}
              {isAdmin && (
                <Line
                  label={`System markup${
                    totalCost > 0 && globalMarkup > 0 ? ` (${systemPct.toFixed(1)}%)` : ''
                  }`}
                >
                  {fmt(globalMarkup)}
                </Line>
              )}
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-sm font-semibold text-zinc-900">Net Selling Price</dt>
                <dd className="text-sm font-semibold text-zinc-900 tabular-nums">
                  {fmt(packagePrice)}
                </dd>
              </div>
              <Line label="Discount">- {fmt(effectiveDiscount)}</Line>
              <Line label="Upsell">+ {fmt(effectiveAdditionalDiscount)}</Line>
            </div>

            {/* Final Selling Price = the taxable base before GST / TCS. The
                "(TTV)" tag is internal jargon, shown to admins only. */}
            <div className="flex items-baseline justify-between gap-3 border-t border-zinc-200 pt-2">
              <dt className="text-sm font-semibold text-zinc-900">
                Final Selling Price{isAdmin ? ' (TTV)' : ''}
              </dt>
              <dd className="text-sm font-semibold text-zinc-900 tabular-nums">
                {fmt(packagePrice - effectiveDiscount + effectiveAdditionalDiscount)}
              </dd>
            </div>

            {/* Taxes + round off. GST / TCS hidden from sales roles. */}
            {(canSeeTax || roundOff !== 0) && (
              <div className="space-y-1.5 border-t border-zinc-100 pt-2">
                {canSeeTax && <Line label={`GST @ ${gstRate}%`}>{fmt(gst)}</Line>}
                {canSeeTax && applyTcs && <Line label={`TCS @ ${tcsRate}%`}>{fmt(tcs)}</Line>}
                {roundOff !== 0 && (
                  <Line label="Round off">
                    {roundOff > 0 ? '+ ' : '- '}
                    {fmt(Math.abs(roundOff))}
                  </Line>
                )}
              </div>
            )}

            <div className="mt-2 flex items-baseline justify-between border-t border-zinc-200 pt-2">
              <span className="text-sm font-semibold text-zinc-900">
                Grand Total
                {/* Admin sees the "(Incl. GST & TCS)" note. Non-admins don't see
                    the GST/TCS breakdown, so instead surface how much TCS the grand
                    total already includes (only when TCS applies). */}
                {isAdmin ? (
                  <>
                    <br />
                    <span className="text-xs text-zinc-500">(Incl. GST &amp; TCS)</span>
                  </>
                ) : (
                  applyTcs &&
                  tcs > 0 && (
                    <>
                      <br />
                      <span className="text-xs text-zinc-500">Incl. TCS {fmt(tcs)}</span>
                    </>
                  )
                )}
              </span>
              <span className="text-base font-bold text-zinc-900 tabular-nums">
                {fmt(grandTotal)}
              </span>
            </div>
          </dl>
        </div>
      </aside>
    </div>
  );
};

const Line = ({
  label,
  children,
  muted = false,
}: {
  label: string;
  children: React.ReactNode;
  muted?: boolean;
}): React.JSX.Element => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className={muted ? 'text-xs text-zinc-400' : 'text-xs text-zinc-500'}>{label}</dt>
    <dd
      className={
        muted ? 'text-xs text-zinc-500 tabular-nums' : 'text-sm text-zinc-700 tabular-nums'
      }
    >
      {children}
    </dd>
  </div>
);

// A discount/upsell value that came from the old percent-of-markup system
// (or otherwise can't be safely re-derived to a fixed-amount master) shows
// here, locked and read-only, instead of the normal dropdown. The stored
// amount is never touched unless the operator explicitly hits "Clear to
// change", which unlocks the dropdown and resets the field to empty.
const LockedMoneyField = ({
  label,
  amountLabel,
  editable,
  onClear,
}: {
  label: string;
  amountLabel: string;
  editable: boolean;
  onClear: () => void;
}): React.JSX.Element => (
  <div>
    <UzInput label={`${label} (locked) ${amountLabel}`} value={amountLabel} disabled readOnly />
    {editable && (
      <UzButton
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 h-6 px-1 text-xs text-sky-600 hover:text-sky-700"
        onClick={onClear}
      >
        Clear to change
      </UzButton>
    )}
  </div>
);

// The field whose lever an ACTIVE reprice solved for (Task 4) - a STRONGER
// lock than `LockedMoneyField` above: there is no "Clear to change" here,
// only "Clear reprice" (calls the DELETE endpoint), because a reprice isn't
// a stored master pick the operator can just swap out - it's a back-solved
// amount tied to the OTHER field being zero and to the reprice marker on the
// itinerary. Clearing unlocks both the Discount and Upsell dropdowns again.
const RepricedLockedField = ({
  label,
  amountLabel,
  editable,
  onClear,
  clearing,
}: {
  label: string;
  amountLabel: string;
  editable: boolean;
  onClear: () => void;
  clearing: boolean;
}): React.JSX.Element => (
  <div>
    <UzInput label={`${label} (repriced) ${amountLabel}`} value={amountLabel} disabled readOnly />
    {editable && (
      <UzButton
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 h-6 px-1 text-xs text-sky-600 hover:text-sky-700"
        onClick={onClear}
        loading={clearing}
      >
        Clear reprice
      </UzButton>
    )}
  </div>
);

// ============================================================================
// Payment plan section (three modes: read-only plan card / admin manual editor
// / non-admin hidden). Once a PaymentPlan exists the manual editor is locked
// out for everyone including admins - the plan is the source of truth.
// ============================================================================

const fmtRupees = (paise: number): string =>
  `₹${paise.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const PaymentPlanSection = ({
  itineraryId,
  isAdmin,
  editable,
  schedule,
  setSchedule,
  setScheduleItem,
  scheduleTotal,
  grandTotal,
  currency,
}: {
  itineraryId: string;
  isAdmin: boolean;
  editable: boolean;
  schedule: PaymentScheduleItem[];
  setSchedule: React.Dispatch<React.SetStateAction<PaymentScheduleItem[]>>;
  setScheduleItem: (i: number, patch: Partial<PaymentScheduleItem>) => void;
  scheduleTotal: number;
  grandTotal: number;
  currency: string;
}): React.JSX.Element => {
  const planQuery = useItineraryPaymentPlan(itineraryId);
  const plan = planQuery.data ?? null;
  // Only admins can create the legacy manual schedule, and only when no
  // PaymentPlan exists yet (pre-publish or non-eligible itinerary). Once a
  // plan exists it is authoritative and the manual editor is hidden for
  // everyone, admin included.
  const showManualEditor = !plan && isAdmin;
  const showEmptyNoteForAgent = !plan && !isAdmin;

  const rupees = (paise: number): number => Math.round(paise / 100);

  return (
    <section className="rounded-lg border border-zinc-200 bg-white">
      <header className="border-b border-zinc-100 px-5 py-3.5">
        <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
          Payment options (customer docket)
        </h3>
        <p className="mt-0.5 text-xs text-zinc-500">
          {plan
            ? 'Installment plan is auto-generated at publish and locked - shown here read-only with paid status.'
            : 'Once you publish, the system auto-creates a 1-3 instalment plan per the policy. Admins can optionally seed a legacy schedule below before publish.'}
        </p>
      </header>
      <div className="space-y-4 px-5 py-5">
        {plan && <PaymentPlanReadOnly plan={plan} />}

        {plan && <InstalmentSplitEditor itineraryId={itineraryId} plan={plan} />}

        {plan && <InstalmentDateEditor itineraryId={itineraryId} plan={plan} />}

        {showEmptyNoteForAgent && (
          <p className="text-xs text-zinc-500">
            No plan yet. It will auto-generate on publish (booking amount, flight, balance
            instalments per the policy).
          </p>
        )}

        {showManualEditor && (
          <div>
            <p className="text-sm font-medium text-zinc-900">Manual instalments (admin)</p>
            <p className="mt-0.5 text-xs text-zinc-400">
              Optional legacy schedule shown on the docket. Once you publish, the auto-generated
              PaymentPlan takes over and this editor disappears for everyone, including admins.
            </p>
            {schedule.length === 0 && (
              <p className="mt-2 text-xs text-zinc-400">
                No installments - the docket shows the full-payment option only.
              </p>
            )}
            <div className="mt-2 space-y-2">
              {schedule.map((item, i) => (
                <div key={i} className="flex flex-wrap items-end gap-2">
                  <div className="min-w-40 flex-1">
                    <UzInput
                      label={i === 0 ? 'Label' : undefined}
                      value={item.label}
                      onChange={(e) => setScheduleItem(i, { label: e.target.value })}
                      placeholder={`e.g. ${['First', 'Second', 'Third'][i] ?? 'Next'} Installment`}
                      disabled={!editable}
                    />
                  </div>
                  <div className="w-36">
                    <UzNumberInput
                      label={i === 0 ? `Amount (${currency})` : undefined}
                      precision={0}
                      min={0}
                      value={rupees(Number(item.amountPaise) || 0)}
                      onValueChange={(v) =>
                        setScheduleItem(i, { amountPaise: String(Math.max(0, (v ?? 0) * 100)) })
                      }
                      disabled={!editable}
                    />
                  </div>
                  <div className="w-40">
                    <UzInput
                      label={i === 0 ? 'Due date' : undefined}
                      type="date"
                      value={item.dueDate ?? ''}
                      onChange={(e) => setScheduleItem(i, { dueDate: e.target.value || null })}
                      disabled={!editable}
                    />
                  </div>
                  {editable && (
                    <UzButton
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 shrink-0 text-zinc-400 hover:text-red-600"
                      onClick={() => setSchedule((s) => s.filter((_, j) => j !== i))}
                      aria-label="Remove installment"
                    >
                      <Trash2 className="h-4 w-4" />
                    </UzButton>
                  )}
                </div>
              ))}
            </div>
            {editable && schedule.length < 12 && (
              <UzButton
                variant="outline"
                size="sm"
                className="mt-2 h-7 text-xs"
                onClick={() =>
                  setSchedule((s) => [
                    ...s,
                    {
                      label: `${['First', 'Second', 'Third', 'Fourth', 'Fifth'][s.length] ?? 'Next'} Installment`,
                      amountPaise: '0',
                      dueDate: null,
                    },
                  ])
                }
              >
                <Plus className="mr-1 h-3 w-3" aria-hidden />
                Add installment
              </UzButton>
            )}
            {schedule.length > 0 && scheduleTotal !== grandTotal && (
              <UzAlert tone="info" className="mt-3">
                Installments add up to {fmtRupees(rupees(scheduleTotal))}; the grand total is{' '}
                {fmtRupees(rupees(grandTotal))}.
              </UzAlert>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const INSTALMENT_STATUS_META: Record<
  AdminPaymentPlanView['instalments'][number]['status'],
  { label: string; className: string }
> = {
  PENDING: { label: 'Pending', className: 'bg-zinc-100 text-zinc-700' },
  ACTIVE: { label: 'Link ready', className: 'bg-sky-100 text-sky-800' },
  PAID: { label: 'Paid', className: 'bg-emerald-100 text-emerald-800' },
  LAPSED: { label: 'Lapsed', className: 'bg-amber-100 text-amber-800' },
  CANCELLED: { label: 'Cancelled', className: 'bg-zinc-100 text-zinc-500' },
};

const PaymentPlanReadOnly = ({ plan }: { plan: AdminPaymentPlanView }): React.JSX.Element => {
  const dueDate = (iso: string): string =>
    new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  const dueDateTime = (iso: string): string =>
    new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  const paise = (v: string): string =>
    `₹${(Number(BigInt(v)) / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Next payable instalment = the first one still owed (not paid / cancelled /
  // lapsed). Only this row carries a live payment link; the ones after it are
  // "locked" until the previous is paid (the plan mints links sequentially).
  const nextPayableSeq =
    plan.instalments.find((i) => i.status === 'PENDING' || i.status === 'ACTIVE')?.seq ?? null;

  const copyPayLink = (url: string, label: string): void => {
    void navigator.clipboard
      .writeText(url)
      .then(() => toast.success(`${label} payment link copied`))
      .catch(() => toast.error('Could not copy the link'));
  };

  return (
    <div>
      {/* Compact header - dropped the "Tier X plan" label and the state
          pill per client feedback; the header now carries just the
          published/travel dates so agents can see the reference window
          at a glance without technical labels. */}
      <div className="mb-3 text-xs text-zinc-500">
        Published on {dueDate(plan.publishedAt)} · Travel starts {dueDate(plan.travelStartDate)} ·{' '}
        {plan.daysToDeparture}d out
      </div>

      <div className="overflow-hidden rounded-lg border border-zinc-200">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs font-semibold text-zinc-600">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Instalment</th>
              <th className="px-3 py-2">Due</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {plan.instalments.map((inst) => {
              const istatus = INSTALMENT_STATUS_META[inst.status];
              // "Non-refundable" chip removed per client feedback - the
              // label itself ("Non-Refundable Booking Amount") already
              // conveys the flag; the chip was redundant chrome.
              return (
                <tr key={inst.id} className="text-zinc-800">
                  <td className="px-3 py-2 text-zinc-500 tabular-nums">{inst.seq}</td>
                  <td className="px-3 py-2">{inst.label}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">
                    {inst.seq === 1
                      ? `Till ${dueDateTime(inst.dueDate)}`
                      : `on or before ${dueDate(inst.dueDate)}`}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">
                    {paise(inst.amountPaise)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span
                      className={
                        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ' +
                        istatus.className
                      }
                    >
                      {istatus.label}
                    </span>
                    {inst.status === 'PAID' && inst.paidAt && (
                      <div className="mt-0.5 text-[10px] text-zinc-400">
                        {dueDateTime(inst.paidAt)}
                      </div>
                    )}
                    {/* Per-instalment payment link to share with the customer -
                        only for the next payable one (links are minted
                        sequentially). Paid / cancelled / lapsed rows show
                        nothing; later unpaid ones show "Locked". */}
                    {inst.status !== 'PAID' &&
                      inst.status !== 'CANCELLED' &&
                      inst.status !== 'LAPSED' && (
                        <div className="mt-1 flex justify-end">
                          {inst.seq === nextPayableSeq ? (
                            inst.status === 'ACTIVE' && inst.paymentUrl ? (
                              <button
                                type="button"
                                onClick={() => copyPayLink(inst.paymentUrl!, inst.label)}
                                className="inline-flex cursor-pointer items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-sky-700 transition-colors hover:bg-sky-50"
                                title="Copy this instalment's payment link to share with the customer"
                              >
                                <Link2 className="h-3 w-3" aria-hidden />
                                Copy pay link
                              </button>
                            ) : inst.linkFailed ? (
                              <span className="text-[10px] font-medium text-rose-500">
                                Link failed
                              </span>
                            ) : (
                              <span className="text-[10px] text-zinc-400">Generating...</span>
                            )
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] text-zinc-400"
                              title="Available after the previous instalment is paid"
                            >
                              <Lock className="h-2.5 w-2.5" aria-hidden />
                              Locked
                            </span>
                          )}
                        </div>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Format a Date to a YYYY-MM-DD string from its local calendar parts. */
const toYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Seller control to lower the FIRST instalment after the itinerary is sent. Only
 * before the plan locks (PREVIEW, nothing paid) and only when the first instalment
 * is not already paid. The new amount must be > 0 and below the auto-calculated
 * first instalment. The difference is handled one of two ways:
 *   - "Create a new installment" (default ON): the shortfall becomes a brand-new
 *     installment on a seller-picked due date (later ones shift down a seq).
 *   - OFF: the shortfall folds into installment 2 and its due date moves one day
 *     earlier (needs an installment 2 to exist).
 * Submitting routes through the reprice-approval queue (kind INSTALMENT_SPLIT):
 * the customer links keep the old amounts until a lead/admin approves (an ADMIN
 * auto-approves).
 */
const InstalmentSplitEditor = ({
  itineraryId,
  plan,
}: {
  itineraryId: string;
  plan: AdminPaymentPlanView;
}): React.JSX.Element | null => {
  const propose = useProposeInstalmentSplit(itineraryId);
  const sorted = useMemo(() => [...plan.instalments].sort((a, b) => a.seq - b.seq), [plan]);
  const i1 = sorted[0];
  const i2 = sorted[1];
  const autoI1Rupees = i1 ? Math.round(Number(i1.amountPaise) / 100) : 0;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<number | undefined>(autoI1Rupees);
  const [createNew, setCreateNew] = useState(true);
  // Default the new installment's date to I2's due date if present, else a week
  // after the first installment's - a sensible "next payment" starting point.
  const defaultNewDate = useMemo(() => {
    if (i2) return new Date(i2.dueDate);
    if (i1) {
      const d = new Date(i1.dueDate);
      d.setDate(d.getDate() + 7);
      return d;
    }
    return new Date();
  }, [i1, i2]);
  const [newDate, setNewDate] = useState<Date | null>(defaultNewDate);

  // Editable only pre-lock and while the first instalment is unpaid.
  const canEdit = plan.status === 'PREVIEW' && !!i1 && i1.status !== 'PAID';
  if (!canEdit) return null;

  const newI1 = value ?? 0;
  const shortfallRupees = Math.max(0, autoI1Rupees - newI1);
  const amountValid = newI1 > 0 && newI1 < autoI1Rupees;
  // FOLD needs an installment 2 to absorb the shortfall; NEW needs a date.
  const modeValid = createNew ? !!newDate : !!i2;
  const valid = amountValid && modeValid;
  const newI2Rupees = i2 ? Math.round(Number(i2.amountPaise) / 100) + shortfallRupees : 0;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    try {
      await propose.mutateAsync({
        firstInstalmentPaise: Math.round(newI1 * 100),
        createNewInstalment: createNew,
        newInstalmentDueDate: createNew && newDate ? toYmd(newDate) : undefined,
      });
      setOpen(false);
    } catch {
      // toast surfaced by the hook
    }
  };

  if (!open) {
    return (
      <div className="flex items-center justify-between rounded-md border border-zinc-200 bg-zinc-50/60 px-4 py-2.5">
        <p className="text-xs text-zinc-600">
          Customer can only pay part of the first installment now? Lower it - the balance becomes a
          new installment (or moves into the next one) (approval required).
        </p>
        <UzButton variant="outline" size="sm" onClick={() => setOpen(true)}>
          Adjust first installment
        </UzButton>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-sky-200 bg-sky-50/50 px-4 py-4">
      <p className="text-sm font-medium text-zinc-900">Lower the first installment</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <UzNumberInput
            name="newFirstInstalment"
            label={`New first installment (${plan.currencyCode})`}
            min={0}
            max={autoI1Rupees}
            precision={0}
            value={value ?? ''}
            onValueChange={(v) => setValue(v ?? undefined)}
            disabled={propose.isPending}
          />
        </div>
        <p className="pb-2 text-xs text-zinc-600">
          Auto-calculated: <strong>{fmtRupees(autoI1Rupees)}</strong>. You can only reduce it.
        </p>
      </div>

      <UzCheckbox
        checked={createNew}
        onCheckedChange={(c) => setCreateNew(Boolean(c))}
        disabled={propose.isPending}
        label="Create a new installment for the difference"
        description={
          createNew
            ? 'The balance becomes a separate installment on the date you pick below.'
            : 'The balance is added to the next installment instead (its due date moves one day earlier).'
        }
      />

      {createNew && (
        <div className="w-56">
          <UzDatePicker
            label="New installment due date"
            value={newDate}
            onValueChange={setNewDate}
            minDate={i1 ? new Date(i1.dueDate) : undefined}
            disabled={propose.isPending}
          />
        </div>
      )}

      {valid && shortfallRupees > 0 && createNew && (
        <UzAlert tone="info">
          {fmtRupees(shortfallRupees)} becomes a new installment
          {newDate ? ` due ${newDate.toLocaleDateString('en-IN')}` : ''}. The customer links keep
          the current amounts until this is approved.
        </UzAlert>
      )}
      {valid && shortfallRupees > 0 && !createNew && (
        <UzAlert tone="info">
          {fmtRupees(shortfallRupees)} moves into the second installment (now{' '}
          {fmtRupees(newI2Rupees)}), and its due date moves one day earlier. The customer links keep
          the current amounts until this is approved.
        </UzAlert>
      )}
      {!amountValid && (
        <p className="text-xs text-red-600">
          Enter an amount greater than 0 and below {fmtRupees(autoI1Rupees)}.
        </p>
      )}
      {amountValid && !modeValid && !createNew && (
        <p className="text-xs text-red-600">
          This plan has a single installment, so tick the box to add the balance as a new one.
        </p>
      )}
      <div className="flex justify-end gap-2">
        <UzButton
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
            setValue(autoI1Rupees);
            setCreateNew(true);
            setNewDate(defaultNewDate);
          }}
          disabled={propose.isPending}
        >
          Cancel
        </UzButton>
        <UzButton
          size="sm"
          onClick={() => void submit()}
          loading={propose.isPending}
          disabled={!valid}
        >
          Send for approval
        </UzButton>
      </div>
    </div>
  );
};

/**
 * Seller control to change the due date of any UNPAID installment - works on a
 * PREVIEW plan or a LOCKED (won) plan, since a date move does not touch amounts.
 * Only PENDING / ACTIVE (unpaid, live) installments are editable; paid, cancelled
 * and lapsed rows are frozen. Submitting routes through the reprice-approval queue
 * (kind INSTALMENT_DATE_CHANGE); an ADMIN auto-approves, everyone else's goes to
 * the approval inbox.
 */
const InstalmentDateEditor = ({
  itineraryId,
  plan,
}: {
  itineraryId: string;
  plan: AdminPaymentPlanView;
}): React.JSX.Element | null => {
  const propose = useProposeInstalmentDateChange(itineraryId);
  // Every UNPAID instalment in the plan hierarchy - PENDING, ACTIVE or LAPSED.
  // Lapsed ones are the whole point (a missed instalment gets a fresh date + link
  // so the customer can still pay); only PAID / CANCELLED are frozen.
  const editable = useMemo(
    () =>
      [...plan.instalments]
        .sort((a, b) => a.seq - b.seq)
        .filter((i) => i.status !== 'PAID' && i.status !== 'CANCELLED'),
    [plan],
  );
  const [open, setOpen] = useState(false);
  // instalmentId -> picked Date / amount (rupees), seeded from the current values.
  const [dates, setDates] = useState<Record<string, Date | null>>({});
  const [amounts, setAmounts] = useState<Record<string, number | undefined>>({});
  const [createNew, setCreateNew] = useState(true);
  const [newDate, setNewDate] = useState<Date | null>(null);

  const origPaise = (inst: AdminPaymentPlanView['instalments'][number]): number =>
    Math.round(Number(inst.amountPaise));
  const editedPaise = (inst: AdminPaymentPlanView['instalments'][number]): number => {
    const rupees = amounts[inst.id];
    return rupees === undefined ? origPaise(inst) : Math.round(rupees * 100);
  };

  if (editable.length === 0) return null;

  const last = editable[editable.length - 1]!;

  const openEditor = (): void => {
    const seedDates: Record<string, Date | null> = {};
    const seedAmts: Record<string, number> = {};
    for (const inst of editable) {
      seedDates[inst.id] = new Date(inst.dueDate);
      seedAmts[inst.id] = origPaise(inst) / 100;
    }
    setDates(seedDates);
    setAmounts(seedAmts);
    setCreateNew(true);
    // Default the new-instalment date to the last instalment's due date.
    setNewDate(new Date(last.dueDate));
    setOpen(true);
  };

  // Plan total is preserved: the leftover (reduced overall) or overflow (raised).
  const currentTotalPaise = editable.reduce((a, i) => a + origPaise(i), 0);
  const newTotalPaise = editable.reduce((a, i) => a + editedPaise(i), 0);
  const deltaPaise = currentTotalPaise - newTotalPaise; // >0 leftover, <0 overflow
  const deltaRupees = deltaPaise / 100;

  const anyDateChange = editable.some(
    (inst) => !!dates[inst.id] && toYmd(dates[inst.id]!) !== toYmd(new Date(inst.dueDate)),
  );
  const anyAmountChange = editable.some((inst) => editedPaise(inst) !== origPaise(inst));
  const hasChanges = anyDateChange || anyAmountChange;

  const allAmountsPositive = editable.every((inst) => editedPaise(inst) > 0);
  const allDatesSet = editable.every((inst) => !!dates[inst.id]);
  // A leftover (amounts reduced overall) can become a new instalment; an overflow
  // (amounts raised beyond the total) always comes off the last instalment.
  const effectiveNew = createNew && deltaPaise > 0;
  const foldLastPaise = editedPaise(last) + deltaPaise;
  const foldBreaksLast = !effectiveNew && deltaPaise !== 0 && foldLastPaise <= 0;

  const valid =
    hasChanges &&
    allAmountsPositive &&
    allDatesSet &&
    !foldBreaksLast &&
    (!effectiveNew || !!newDate);

  const submit = async (): Promise<void> => {
    if (!valid) return;
    try {
      await propose.mutateAsync({
        changes: editable.map((inst) => ({
          instalmentId: inst.id,
          dueDate: toYmd(dates[inst.id]!),
          amountPaise: editedPaise(inst),
        })),
        createNewInstalment: effectiveNew,
        newInstalmentDueDate: effectiveNew && newDate ? toYmd(newDate) : undefined,
      });
      setOpen(false);
    } catch {
      // toast surfaced by the hook
    }
  };

  if (!open) {
    return (
      <div className="flex items-center justify-between rounded-md border border-zinc-200 bg-zinc-50/60 px-4 py-2.5">
        <p className="text-xs text-zinc-600">
          Need to change an installment&apos;s date or amount? You can edit any unpaid installment -
          the plan total stays the same (approval required).
        </p>
        <UzButton variant="outline" size="sm" onClick={openEditor}>
          Change installments
        </UzButton>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-sky-200 bg-sky-50/50 px-4 py-4">
      <p className="text-sm font-medium text-zinc-900">Change installment dates &amp; amounts</p>
      <div className="space-y-3">
        {editable.map((inst) => (
          <div key={inst.id} className="flex flex-wrap items-end gap-3">
            <div className="min-w-32 flex-1">
              <p className="text-xs font-medium text-zinc-700">
                #{inst.seq} {inst.label}
              </p>
              <p className="text-[11px] text-zinc-500">
                Now {fmtRupees(origPaise(inst) / 100)} ·{' '}
                {new Date(inst.dueDate).toLocaleDateString('en-IN')}
              </p>
            </div>
            <div className="w-40">
              <UzNumberInput
                label={`Amount (${plan.currencyCode})`}
                min={0}
                precision={2}
                value={amounts[inst.id] ?? ''}
                onValueChange={(v) =>
                  setAmounts((prev) => ({ ...prev, [inst.id]: v ?? undefined }))
                }
                disabled={propose.isPending}
              />
            </div>
            <div className="w-48">
              <UzDatePicker
                label="Due date"
                value={dates[inst.id] ?? null}
                onValueChange={(d) => setDates((prev) => ({ ...prev, [inst.id]: d }))}
                disabled={propose.isPending}
              />
            </div>
          </div>
        ))}
      </div>

      {/* How the leftover / overflow is reconciled so the plan total never moves. */}
      {deltaPaise !== 0 && (
        <UzCheckbox
          checked={createNew}
          onCheckedChange={(c) => setCreateNew(Boolean(c))}
          disabled={propose.isPending || deltaPaise < 0}
          label="Create a new installment for the difference"
          description={
            deltaPaise < 0
              ? 'The amounts exceed the remaining total, so there is no leftover for a new installment - the overflow is taken off the last installment.'
              : createNew
                ? 'The leftover becomes a separate installment on the date you pick below.'
                : 'The leftover is added to the last installment instead.'
          }
        />
      )}
      {effectiveNew && (
        <div className="w-56">
          <UzDatePicker
            label="New installment due date"
            value={newDate}
            onValueChange={setNewDate}
            disabled={propose.isPending}
          />
        </div>
      )}

      {effectiveNew && (
        <UzAlert tone="info">
          A new installment of {fmtRupees(deltaRupees)} will be added
          {newDate ? ` due ${newDate.toLocaleDateString('en-IN')}` : ''}. The plan total stays{' '}
          {fmtRupees(currentTotalPaise / 100)}.
        </UzAlert>
      )}
      {deltaPaise !== 0 && !effectiveNew && !foldBreaksLast && (
        <UzAlert tone="info">
          {deltaPaise < 0
            ? `${fmtRupees(-deltaRupees)} over the total is taken off `
            : `${fmtRupees(deltaRupees)} leftover is added to `}
          #{last.seq} {last.label} (now {fmtRupees(foldLastPaise / 100)}) so the plan total stays{' '}
          {fmtRupees(currentTotalPaise / 100)}.
        </UzAlert>
      )}
      {foldBreaksLast && (
        <p className="text-xs text-red-600">
          This would make the last installment zero or negative. Adjust the amounts.
        </p>
      )}
      {!allAmountsPositive && (
        <p className="text-xs text-red-600">Every installment amount must be greater than 0.</p>
      )}

      <div className="flex justify-end gap-2">
        <UzButton
          variant="ghost"
          size="sm"
          onClick={() => setOpen(false)}
          disabled={propose.isPending}
        >
          Cancel
        </UzButton>
        <UzButton
          size="sm"
          onClick={() => void submit()}
          loading={propose.isPending}
          disabled={!valid}
        >
          Send for approval
        </UzButton>
      </div>
    </div>
  );
};
